import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type Tone = 'info' | 'success' | 'warn' | 'error';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
}

interface ToastApi {
  show: (message: string, tone?: Tone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 0;
const TONE_CLASS: Record<Tone, string> = {
  info: 'bg-slate-900 text-white',
  success: 'bg-ok text-white',
  warn: 'bg-warn text-slate-900',
  error: 'bg-danger text-white',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, tone: Tone = 'info') => {
    const id = ++nextId;
    setItems((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 top-4 z-[1100] flex w-80 max-w-full flex-col gap-2"
      >
        {items.map((t) => (
          <ToastBubble key={t.id} item={t} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastBubble({ item }: { item: ToastItem }) {
  const [enter, setEnter] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEnter(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      role="status"
      className={
        'pointer-events-auto rounded-lg px-4 py-2.5 text-sm font-medium shadow-card transition duration-200 ' +
        TONE_CLASS[item.tone] +
        ' ' +
        (enter ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0')
      }
    >
      {item.message}
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider.');
  return ctx;
}
